// Agg-context functions — `sum`, `mean`, `min`, `max`, `count`, `first`,
// `last`, `value`, `values`, `timestamps`. Reachable ONLY inside
// `(aggregate …)` positional pairs and `(time-bucket :agg …)` — they
// are registered in `aggMeta`, NOT `opMeta`, so calling at top level
// surfaces `UnknownOpError` via the existing context-restriction
// mechanism (same pattern as `pipelineVerbs` for transform verbs).
//
// Each func declares a `compileAggExpr` callback that translates the
// raw op call into an `AggSpec` (engine-side IR). The parent verb's
// apply (`aggregate`, `time-bucket`) collects the specs into a rollup
// map and hands off to `runtime/arquero.ts::compileAggSpec` for the
// arquero translation — preserves the §8.1 boundary (only
// `runtime/arquero.ts` knows about arquero).

import { z } from 'zod';

import { UnknownOpError } from '../../errors.ts';
import { opCallPos } from '../../normalize.ts';
import {
  type AggOpMeta,
  type AggSpec,
  type ArqueroAggOpName,
  aggMeta,
  createOpTable,
  defineScope,
  type NameTable,
  type RawOpCall,
  type Scope,
} from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { compileAggSpec } from '../../runtime/arquero.ts';

// `{ $sym: <colname> }` shape — agg-context funcs accept a bare column
// symbol (an AST `symbolRef`). Cross-dataset refs via `(col …)` are
// intentionally NOT supported here: agg funcs run inside the parent
// verb's grouping context, which is always anchored at the threaded
// dataset.
const ColSymbol = z.object({
  // biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
  $sym: z.string().min(1),
});

/** Read the column name from a single-column-symbol arg. */
const colNameOf = (call: RawOpCall): string => {
  const arg = (call.params ?? [])[0] as { $sym: string };
  return arg.$sym;
};

/**
 * Factory: build a standard column-aggregation op (sum, mean, min, max,
 * first, last). Each one takes a single column-symbol arg and emits
 * an `AggSpec` of kind `col-agg`. Zero deviation across the six —
 * different `name` literal, identical schema and apply shape.
 */
const makeColAggOp = <N extends ArqueroAggOpName>(name: N) => {
  return z
    .object({
      name: z.literal(name),
      params: z.tuple([ColSymbol]),
    })
    .describe(`Aggregate the named column with ${name}.`)
    .register(aggMeta, {
      effect: 'pure',
      example: { expr: `(${name} revenue)` },
      compileAggExpr: (call): AggSpec => ({ kind: 'col-agg', op: name, col: colNameOf(call) }),
    } satisfies AggOpMeta);
};

export const sumOp = makeColAggOp('sum');
export const meanOp = makeColAggOp('mean');
export const minOp = makeColAggOp('min');
export const maxOp = makeColAggOp('max');
export const firstOp = makeColAggOp('first');
export const lastOp = makeColAggOp('last');

/**
 * `(count)` — zero-arg row count. Useful in `(aggregate (n (count))
 * :by [region])` to count rows per group.
 */
export const countOp = z
  .object({
    name: z.literal('count'),
    params: z.tuple([]),
  })
  .describe('Count rows in the current group (or in the whole dataset when no `:by` is set).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(count)' },
    compileAggExpr: (): AggSpec => ({ kind: 'row-count' }),
  } satisfies AggOpMeta);

/**
 * `(value)` — TS-ref shorthand for the latest value of a per-cell
 * timeseries Dataset's `value` column. Only meaningful when the
 * agg-context runs inside a per-cell lifting (see `evalVerbCall`'s
 * nested-dataset branch) over a TS-ref column: the inner schema is
 * `{ timestamp, value, isString, unit }`, and `(value)` picks
 * `last(value)` (timestamps are sorted ascending by convention).
 *
 * Outside that per-cell context the column `value` doesn't exist —
 * the aggregate's `referencedColumns` will not nominate this func
 * for any column, but `runtime/arquero.ts` will fail at rollup
 * compilation if the source dataset has no `value` column. This is
 * the LLM-facing pattern: pair `(value)` with TS-ref columns
 * exclusively.
 */
export const valueOp = z
  .object({
    name: z.literal('value'),
    params: z.tuple([]),
  })
  .describe('Latest `value` of a per-cell timeseries Dataset (TS-ref column).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(value)' },
    compileAggExpr: (): AggSpec => ({ kind: 'col-pick', col: 'value', pick: 'last' }),
  } satisfies AggOpMeta);

/**
 * `(values)` — collect the entire `value` column of a per-cell TS-ref
 * Dataset into an array. Same per-cell precondition as `(value)`.
 */
export const valuesOp = z
  .object({
    name: z.literal('values'),
    params: z.tuple([]),
  })
  .describe('Collect the full `value` array of a per-cell timeseries Dataset (TS-ref column).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(values)' },
    compileAggExpr: (): AggSpec => ({ kind: 'col-array', col: 'value' }),
  } satisfies AggOpMeta);

/**
 * `(timestamps)` — sibling of `(values)`: collect the `timestamp` column
 * of a per-cell TS-ref Dataset into an array.
 */
export const timestampsOp = z
  .object({
    name: z.literal('timestamps'),
    params: z.tuple([]),
  })
  .describe('Collect the full `timestamp` array of a per-cell timeseries Dataset (TS-ref column).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(timestamps)' },
    compileAggExpr: (): AggSpec => ({ kind: 'col-array', col: 'timestamp' }),
  } satisfies AggOpMeta);

/** All agg-context func schemas, ordered for stable doc rendering. */
export const aggContextFuncs = [
  sumOp,
  meanOp,
  minOp,
  maxOp,
  countOp,
  firstOp,
  lastOp,
  valueOp,
  valuesOp,
  timestampsOp,
] as const;
/** Union of every agg-context func schema — generic agg-schema type. */
export type AggFuncSchema = (typeof aggContextFuncs)[number];

/**
 * Agg-context scope — packaged registry + schemas list for the doc
 * walker and for runtime dispatch tables. Parent verbs (`aggregate`,
 * `time-bucket`) declare `childScopes: [aggContextScope]` so their
 * children resolve from this scope's registry. Adding a new agg
 * func is one `aggMeta.register` + one append to `aggContextFuncs`
 * — no edits to evaluator or doc generator.
 */
export const aggContextScope: Scope<AggOpMeta> = defineScope({
  name: 'Aggregation functions',
  description:
    'Reachable inside `(aggregate …)` positional pairs and `(time-bucket :agg …)`. Each func collapses a group of rows (or a per-cell timeseries Dataset) into a single value.',
  registry: aggMeta,
  schemas: aggContextFuncs,
});
/**
 * Zod fragment for an `(out-name (agg-call …))` pair — the positional
 * arg shape shared by `aggregate` and `time-bucket :agg`. Hoisted as
 * the SSOT so both verbs use byte-identical validation.
 */
export const AggPairSchema = z.object({
  name: z.string().min(1),
  params: z.tuple([
    z.object({
      name: z.string().min(1),
      params: z.array(z.unknown()).optional(),
    }),
  ]),
});

/** Module-level agg-func name table — built once at module load and
 * shared by every verb that dispatches into the agg-context. */
export const AGG_FUNCS_TABLE: NameTable = createOpTable(aggContextScope.schemas as readonly z.ZodObject[]);

/** Compiled agg pair — the (out-name, manual reducer) record consumed
 * by `aggregate` / `time-bucket` apply when iterating rolled-up rows. */
export type CompiledAggPair = {
  readonly outName: string;
  readonly reduce: (rows: readonly Record<string, unknown>[]) => unknown;
};

/**
 * Compile a single `(out-name (agg-call …))` pair into a
 * `CompiledAggPair`. Routes through `AGG_FUNCS_TABLE` for the
 * agg-func schema lookup, then `aggMeta` for the func's
 * `compileAggExpr` → `AggSpec` → `AggReducer`.
 *
 * SSOT for "translate an (agg-call) opcall into a per-group reducer"
 * — consumed by both `aggregate.ts` and `time-bucket.ts`. Adding a
 * new agg-context verb is one import.
 */
export const compileAggPair = (
  pair: {
    readonly name: string;
    readonly params: readonly { readonly name: string; readonly params?: readonly unknown[] }[];
  },
  basePath: string,
): CompiledAggPair => {
  const aggCall = pair.params[0];
  const path = paramPath(paramPath(basePath, 0), 0);
  if (aggCall === undefined) {
    throw new UnknownOpError('', Array.from(AGG_FUNCS_TABLE.keys()), opCallPos(new Map(), path));
  }
  const schema = AGG_FUNCS_TABLE.get(aggCall.name);
  if (schema === undefined) {
    throw new UnknownOpError(aggCall.name, Array.from(AGG_FUNCS_TABLE.keys()), opCallPos(new Map(), path));
  }
  const meta = aggMeta.get(schema);
  if (meta === undefined) {
    throw new Error(`compileAggPair: schema for \`${aggCall.name}\` not registered in aggMeta`);
  }
  const spec = meta.compileAggExpr({ name: aggCall.name, params: aggCall.params ?? [] }, path);
  return { outName: pair.name, reduce: compileAggSpec(spec) };
};

/**
 * Synthesise the positional args for a bare agg symbol's equivalent
 * `(agg-call)` — SSOT for the "bare symbol ⇒ `(<sym> value)` pair"
 * rule. `count` is zero-arg (`row-count`); every other scalar agg
 * takes the TS-ref inner `value` column. Used both to DERIVE the bare
 * symbol set and to COMPILE it, so the two can never drift.
 */
// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved (AST symbolRef shape)
const VALUE_COL_SYM = { $sym: 'value' } as const;
const bareAggArgs = (symbol: string): readonly unknown[] => (symbol === 'count' ? [] : [VALUE_COL_SYM]);

/**
 * Bare agg symbols valid in `time-bucket`'s per-cell `:agg` vector
 * form (PHASE_4_PREP.md D11.2). DERIVED from the agg registry — the
 * exact set of agg-context funcs whose `compileAggExpr` yields a
 * scalar reducer (`col-agg` / `row-count`) over the inner `value`
 * column, i.e. every func EXCEPT the TS-ref collectors
 * (`value` / `values` / `timestamps`, which yield `col-pick` /
 * `col-array`). No hand-maintained literal: adding a scalar agg func
 * to `aggContextFuncs` makes it bare-usable by construction, and the
 * bare form can never name a func the pair form rejects.
 */
export const BARE_AGG_SYMBOLS: readonly string[] = Array.from(AGG_FUNCS_TABLE.entries()).flatMap(([name, schema]) => {
  const meta = aggMeta.get(schema);
  if (meta === undefined) {
    return [];
  }
  const spec = meta.compileAggExpr({ name, params: bareAggArgs(name) }, 'p');
  return spec.kind === 'col-agg' || spec.kind === 'row-count' ? [name] : [];
});
export type BareAggSymbol = string;

/** Is `s` one of the bare per-cell `:agg` symbols? */
export const isBareAggSymbol = (s: string): s is BareAggSymbol => BARE_AGG_SYMBOLS.includes(s);

/**
 * Compile a bare agg symbol (per-cell `:agg` form) into a
 * `CompiledAggPair` over the TS-ref inner `value` column, output-named
 * `<liftColumn>-<symbol>` (D11.2). Delegates to `compileAggPair` — the
 * SSOT pair compiler — by synthesising the equivalent
 * `(<liftColumn>-<symbol> (<symbol> value))` pair, so the bare form
 * and the `(out (agg col))` pair form share ONE aggregation source
 * (registry lookup + `compileAggExpr` + `compileAggSpec`).
 */
export const compileBareAgg = (symbol: BareAggSymbol, liftColumn: string): CompiledAggPair =>
  compileAggPair({ name: `${liftColumn}-${symbol}`, params: [{ name: symbol, params: bareAggArgs(symbol) }] }, 'p');
